import { useCallback, useMemo } from 'react';
import {
  endOfDay,
  endOfToday,
  startOfDay,
  subDays,
  isValid,
  parse,
  format,
  isBefore,
  isAfter,
} from 'date-fns';
import useQueryString from './use-query-string';

type DateRangeQuery = {
  startDate: string;
  endDate: string;
};

export type DateRangeType = {
  startDate: Date;
  endDate: Date;
  timezone: string;
};

const DATE_FORMAT = 'MM-dd-yyyy';
const DEFAULT_END = endOfToday();
const DEFAULT_START = subDays(startOfDay(DEFAULT_END), 30);
const DATE_SEPARATOR = '..';

const parseDateRange = (value: string | null): DateRangeQuery | null => {
  if (!value) return null;
  const [startDate, endDate] = value.split(DATE_SEPARATOR);
  if (!startDate || !endDate) return null;
  return { startDate, endDate };
};

const serializeDateRange = (value: DateRangeQuery | null): string | null => {
  if (!value) return null;
  return `${value.startDate}${DATE_SEPARATOR}${value.endDate}`;
};

const validateDateRange = (
  value: DateRangeQuery | null,
  minDate: Date,
  maxDate: Date
): boolean => {
  if (!value) return false;

  const startDate = parse(value.startDate, DATE_FORMAT, new Date());
  const endDate = parse(value.endDate, DATE_FORMAT, new Date());

  // Check if the dates are valid
  if (!isValid(startDate) || !isValid(endDate)) return false;

  // Check if the dates are within the range
  if (isBefore(startDate, minDate) || isAfter(startDate, maxDate)) return false;
  if (isBefore(endDate, minDate) || isAfter(endDate, maxDate)) return false;

  // Check if the start date is before the end date
  return !isAfter(startDate, endDate);
};

const timezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;

export function useURLDateRange(
  minDate: Date = DEFAULT_START,
  maxDate: Date = DEFAULT_END
) {
  const [dateRangeQuery, setDateRangeQuery] = useQueryString<{
    startDate: string;
    endDate: string;
  } | null>('dateRange', null, {
    parse: parseDateRange,
    serialize: serializeDateRange,
    validate: value => validateDateRange(value, minDate, maxDate),
  });

  const dateRange: DateRangeType = useMemo(
    () => ({
      startDate: dateRangeQuery
        ? parse(dateRangeQuery.startDate, DATE_FORMAT, new Date())
        : minDate,
      endDate: dateRangeQuery
        ? endOfDay(parse(dateRangeQuery.endDate, DATE_FORMAT, new Date()))
        : maxDate,
      timezone,
    }),
    [dateRangeQuery, minDate, maxDate]
  );

  const handleDateRangeChange = useCallback(
    (range: { startDate: Date; endDate: Date }) => {
      setDateRangeQuery({
        startDate: format(range.startDate, DATE_FORMAT),
        endDate: format(range.endDate, DATE_FORMAT),
      });
    },
    [setDateRangeQuery]
  );

  return {
    dateRange,
    handleDateRangeChange,
  };
}
